// Sending a photo — the browser half of STACK.md §9.
//
// Replaces `enviarUma()` from the old Formularios.tsx, keeping the same
// contract (null on success, a short Portuguese message on failure) and the
// same 3-at-a-time worker pool.
//
// The flow changed shape, and for the better:
//
//   before:  compress → build the key CLIENT-side → upload to Supabase →
//            insert the row → on failure, remember to delete the orphan object
//
//   now:     compress → ask the API to sign (it generates the key) →
//            PUT straight to R2 → confirm → create the row
//
// Two things fall out of that. The object key is no longer attacker-influenced
// — the API builds it from a uuid and never from the filename. And orphan
// cleanup disappears from the client: a failure at any step just leaves a
// `pending` media row that the nightly sweep collects, so there is no
// compensating delete to get right.

import {
  IMAGE_TYPES,
  MAX_SIZE,
  type UploadKind,
} from "@historia/shared/validation";
import type { SignUploadResponse } from "@historia/shared/contracts";
import { api, ApiError } from "@/api/client";
import { comprimirImagem } from "@/lib/comprimirImagem";

/** How many uploads run at once. Three keeps a phone's connection usable. */
const CONCURRENCY = 3;

export interface UploadResult {
  /** The media id to attach to a photo/illustration row. */
  mediaId: string;
}

/**
 * Compress → sign → PUT → confirm. Returns the mediaId to attach.
 * Throws an ApiError whose message is safe to show.
 */
export async function uploadFile(file: File, kind: UploadKind): Promise<UploadResult> {
  // Same browser-side compression as before: canvas resize to 2000px, WebP.
  const optimised = await comprimirImagem(file);

  // 1 — ask for permission. The API validates type and size, generates the
  //     object key itself, and records a `pending` row.
  const signed = await api.post<SignUploadResponse>("/uploads/sign", {
    kind,
    filename: optimised.name,
    contentType: optimised.type,
    size: optimised.size,
  });

  // 2 — the bytes go straight to R2. They never pass through the API, so a
  //     40 MB photo doesn't touch the VPS's memory or bandwidth.
  //
  //     No Authorization header here: the URL carries its own signature, and
  //     an extra header would break it. Content-Type must match what was
  //     signed exactly or R2 rejects the request.
  const put = await fetch(signed.uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": optimised.type },
    body: optimised,
  });

  if (!put.ok) {
    throw new ApiError("INTERNAL", "Não foi possível enviar a imagem. Tenta novamente.", put.status);
  }

  // 3 — confirm. The API HEADs the object, checks the size it ACTUALLY turned
  //     out to be, and flips the row to `ready`.
  await api.post(`/uploads/${signed.mediaId}/confirm`);

  return { mediaId: signed.mediaId };
}

export interface FileCheck {
  valid: File[];
  /** Human-readable reasons, e.g. "ferias.pdf (formato)". */
  rejected: string[];
}

/**
 * Splits a selection into what can be sent and what can't.
 *
 * Deliberately does NOT block the whole batch on one bad file — picking twenty
 * photos and being told "no" because one is a PDF is a miserable experience.
 * The limits come from @historia/shared so they can't drift from the API's.
 */
export function checkFiles(files: File[], kind: UploadKind): FileCheck {
  const max = MAX_SIZE[kind];
  const maxMb = Math.round(max / 1024 / 1024);
  const valid: File[] = [];
  const rejected: string[] = [];

  for (const file of files) {
    if (!(IMAGE_TYPES as readonly string[]).includes(file.type)) {
      rejected.push(`${file.name} (formato)`);
    } else if (file.size > max) {
      rejected.push(`${file.name} (>${maxMb} MB)`);
    } else {
      valid.push(file);
    }
  }

  return { valid, rejected };
}

export interface BatchResult {
  /** mediaIds, in the order the uploads finished. */
  mediaIds: string[];
  /** Files that failed, with a short reason each. */
  failures: string[];
}

/**
 * Uploads several files with a bounded worker pool, reporting progress.
 * One failure doesn't stop the others.
 */
export async function uploadBatch(
  files: File[],
  kind: UploadKind,
  onProgress?: (done: number, total: number) => void,
): Promise<BatchResult> {
  const mediaIds: string[] = [];
  const failures: string[] = [];
  let done = 0;
  let next = 0;

  async function worker(): Promise<void> {
    while (next < files.length) {
      const file = files[next++];
      try {
        const result = await uploadFile(file, kind);
        mediaIds.push(result.mediaId);
      } catch (error) {
        const reason = error instanceof ApiError ? error.message : "falha no envio";
        failures.push(`${file.name} (${reason})`);
      }
      done += 1;
      onProgress?.(done, files.length);
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(CONCURRENCY, files.length) }, () => worker()),
  );

  return { mediaIds, failures };
}
